import { createContext, useCallback, useContext, useState, type JSX, type ReactNode } from 'react';

/**
 * Lets a page fill the LEFT (breadcrumb) region of the global top nav without
 * taking over the whole bar the way {@link PageHeaderSlot} does — the
 * persistent right-hand controls (⌘K, theme, tenant switcher) stay put.
 *
 * Two separate contexts on purpose:
 * - the setter context value is a stable callback, so a producer (e.g. PpShell)
 *   that only needs to push crumbs never re-renders when the crumbs change;
 * - the node context is consumed by a single leaf ({@link TopNavCrumbsOutlet}),
 *   so updating the crumbs re-renders only that leaf — not the page subtree —
 *   which is what keeps producers from looping.
 */
const noop = (): void => undefined;

const TopNavCrumbNodeContext = createContext<ReactNode | null>(null);
const TopNavCrumbSetContext = createContext<(node: ReactNode | null) => void>(noop);

export function TopNavCrumbProvider({ children }: { children: ReactNode }): JSX.Element {
  const [node, setNode] = useState<ReactNode>(null);
  const setCrumbs = useCallback((n: ReactNode | null) => setNode(n ?? null), []);
  return (
    <TopNavCrumbSetContext.Provider value={setCrumbs}>
      <TopNavCrumbNodeContext.Provider value={node}>{children}</TopNavCrumbNodeContext.Provider>
    </TopNavCrumbSetContext.Provider>
  );
}

/** Stable setter — safe to depend on in effects without triggering re-renders. */
export function useSetTopNavCrumbs(): (node: ReactNode | null) => void {
  return useContext(TopNavCrumbSetContext);
}

export function useTopNavCrumbNode(): ReactNode | null {
  return useContext(TopNavCrumbNodeContext);
}
